import { Box, Stack, Card, CardContent, Typography, Chip, Divider, Tooltip, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SignalCellularNodataOutlinedIcon from '@mui/icons-material/SignalCellularNodataOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import SpeedIcon from '@mui/icons-material/Speed';
import OpacityIcon from '@mui/icons-material/Opacity';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import MemoryIcon from '@mui/icons-material/Memory';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
// import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useLanguage } from '../../../utils/i18n';
import dayjs from 'dayjs';

export const statusColor = (rawVehicle, isLoaded = false) => {
  if (!rawVehicle) return 'default';
  const data = rawVehicle.raw || rawVehicle;
  const status = (data.status || rawVehicle.status || '').toString().toLowerCase();
  const gps = (data.gps || rawVehicle.gps || '').toString().toLowerCase();
  const engine = (data.engine || rawVehicle.engine || '').toString().toUpperCase();
  const speed = Number(data.speed || rawVehicle.speed) || 0;

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = data.spatial_id > 0 && data.spatial_max_speed > 0 && speed > data.spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = data.spatial_id > 0 && data.spatial_min_speed > 0 && speed < data.spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gps.includes('updated') || gps.includes('update');

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'success';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'success';

  if (status.includes('delay') || gps.includes('delay')) return 'error';
  if (status.includes('maintenance')) return 'error';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'success';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'warning';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'primary';
    return 'default';
  }

  if (speed > 0 && isGpsUpdated) return 'success';
  if (isAtPolygon) return 'primary';

  return 'default';
};

export const getStatusLabel = (rawVehicle, isLoaded = false) => {
  if (!rawVehicle) return '-';
  // console.log('rawVehicle:', rawVehicle);
  const data = rawVehicle.raw || rawVehicle;
  const status = (data.status || rawVehicle.status || '').toString().toLowerCase();
  const gps = (data.gps || rawVehicle.gps || '').toString().toLowerCase();
  const engine = (data.engine || rawVehicle.engine || '').toString().toUpperCase();
  const speed = Number(data.speed || rawVehicle.speed) || 0;

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = data.spatial_id > 0 && data.spatial_max_speed > 0 && speed > data.spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = data.spatial_id > 0 && data.spatial_min_speed > 0 && speed < data.spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gps.includes('updated') || gps.includes('update');

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'Moving';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'Moving';

  if (gps.includes('delay')) {
    if (gps.includes('24')) return 'Delay > 24h';
    if (gps.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (status.includes('delay')) {
    if (status.includes('24')) return 'Delay > 24h';
    if (status.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (status.includes('maintenance')) return 'Maintenance';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'Moving';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'Idle';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'Parked';
    return 'Stopped';
  }

  if (speed > 0 && isGpsUpdated) return 'Moving';
  if (isAtPolygon) return 'Parked';

  return 'Stopped';
};

export default function VehicleCard({ v, isFollowed, onFollowVehicle }) {
  const { t } = useLanguage();
  const isLoaded = v.isLoaded || false;
  const isOffline =
    String(v?.gsm_signal ?? '').toUpperCase() === 'Bad' ||
    String(v?.status ?? '')
      .toLowerCase()
      .includes('offline') ||
    String(v?.status ?? '')
      .toLowerCase()
      .includes('delay');

  const canbus = v?.raw?.raw?.canbus || {};

  const engineTemp = canbus.canEngineTemp;
  const ecuTemp = canbus.canEcuTemp;
  const batteryPct = canbus.canBatteryPercentage;
  const batteryHealth = canbus.canBatteryHealth;
  const adBluePct = canbus.canAdBlueLevelPercentage;

  const hasTempData = engineTemp || ecuTemp;
  const hasBatteryData = batteryPct || batteryHealth;
  const hasAdBlueData = adBluePct != null && adBluePct !== '';

  const formattedTime =
    v.last_location_time && v.last_location_time !== '-' ? dayjs(v.last_location_time).format('DD/MM/YY HH:mm:ss') : '-';

  const formattedTooltipTime =
    v.last_location_time && v.last_location_time !== '-' ? dayjs(v.last_location_time).format('DD-MM-YYYY HH:mm:ss') : '-';

  return (
    <Card
      variant="outlined"
      sx={{
        width: 300,
        borderRadius: 2,
        border: 1,
        bgcolor: '#ffffff',
        boxShadow: '0 6px 18px rgba(8,14,30,0.04)',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-2px)' }
      }}
    >
      <CardContent sx={{ py: 1.25, px: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {v.activeViolation && (
              <Tooltip title={v.activeViolation.message}>
                <WarningAmberOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: 'error.main',
                    animation: 'pulseViolation 1.5s infinite',
                    '@keyframes pulseViolation': {
                      '0%': { transform: 'scale(1)', opacity: 1 },
                      '50%': { transform: 'scale(1.2)', opacity: 0.6 },
                      '100%': { transform: 'scale(1)', opacity: 1 }
                    }
                  }}
                />
              </Tooltip>
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              {v.fleet_name}
            </Typography>
          </Box>
          <Tooltip title={isFollowed ? 'Unfollow' : 'Follow'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onFollowVehicle && onFollowVehicle(v.id);
              }}
              sx={{ p: 0.5, color: isFollowed ? '#485D9A' : 'text.secondary' }}
            >
              {isFollowed ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isOffline ? (
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                <Tooltip title="Signal Offline">
                  <SignalCellularNodataOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', cursor: 'pointer' }} />
                </Tooltip>
                <Tooltip title="Location Invalid">
                  <LocationOffIcon sx={{ fontSize: 20, color: 'error.main', cursor: 'pointer' }} />
                </Tooltip>
                {formattedTime !== '-' && (
                  <Tooltip title={`Last Update: ${formattedTooltipTime}`}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', cursor: 'pointer' }}>
                      {formattedTime}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                <Tooltip title="Signal Online">
                  <SignalCellularAltIcon sx={{ fontSize: 20, color: 'success.main', cursor: 'pointer' }} />
                </Tooltip>
                <Tooltip title="Location Valid">
                  <LocationOnIcon sx={{ fontSize: 20, color: 'primary.main', cursor: 'pointer' }} />
                </Tooltip>
                {formattedTime !== '-' && (
                  <Tooltip title={`Last Update: ${formattedTooltipTime}`}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', cursor: 'pointer' }}>
                      {formattedTime}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          <Stack spacing={0.5} alignItems="flex-end">
            <Chip
              variant="outlined"
              label={getStatusLabel(v, isLoaded)}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem' }}
              color={statusColor(v, isLoaded)}
            />
          </Stack>
        </Stack>
        {hasTempData && (
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, bgcolor: '#f8fafc', px: 1, py: 0.5, borderRadius: 1 }}>
            {engineTemp && (
              <Tooltip title={t('mapsTracking.engineTemp', 'Suhu Engine')}>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <DeviceThermostatIcon sx={{ fontSize: 14, color: 'error.main' }} />
                  <Typography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
                    {engineTemp}°C
                  </Typography>
                </Stack>
              </Tooltip>
            )}
            {ecuTemp && (
              <Tooltip title={t('mapsTracking.ecuTempToolTip', 'Suhu ECU')}>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <MemoryIcon sx={{ fontSize: 14, color: 'info.main' }} />
                  <Typography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
                    {ecuTemp}°C
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Stack>
        )}

        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        {/* --- KONTEN BAWAH (SPEED, TYPE, FUEL/BATTERY, DRIVER) --- */}
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ width: '100%' }}>
          {/* KOLOM KIRI (Speed & Type) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title="Speed">
              <Stack direction="row" spacing={1} alignItems="center">
                <SpeedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2">{v.raw?.raw?.speed ?? '-'} Km/H</Typography>
              </Stack>
            </Tooltip>
            <Tooltip title={`Vehicle Type: ${v.vehicle_type ?? '—'}`}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <LocalShippingOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" noWrap sx={{ width: '100%' }}>
                  {v.vehicle_type ?? '—'}
                </Typography>
              </Stack>
            </Tooltip>
          </Box>

          {/* KOLOM KANAN (BBM/Battery & Driver) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* KONDISI: RENDER BATERAI JIKA EV, JIKA BUKAN RENDER BBM */}
            {hasBatteryData ? (
              <Stack direction="row" alignItems="center">
                {batteryPct && (
                  <Tooltip title="Battery Percentage">
                    <Stack direction="row" alignItems="center">
                      <BatteryChargingFullIcon fontSize="small" sx={{ color: 'success.main' }} />
                      <Typography variant="body2" fontWeight="bold">
                        {batteryPct}%
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
                {batteryHealth && (
                  <Tooltip title="Battery Health">
                    <Stack direction="row" alignItems="center">
                      <HealthAndSafetyIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      <Typography variant="body2" fontWeight="bold">
                        {batteryHealth}%
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
              </Stack>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Fuel">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LocalGasStationOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant={hasAdBlueData ? 'caption' : 'body2'} sx={{ fontSize: hasAdBlueData ? '0.65rem' : '0.75rem' }}>
                      {v.raw?.raw?.fuel ?? '-'} L
                    </Typography>
                  </Stack>
                </Tooltip>
                {hasAdBlueData && (
                  <Tooltip title="Adblue">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {(() => {
                        const val = Number(adBluePct);
                        let color = 'text.secondary';
                        if (!isNaN(val)) {
                          if (val >= 20)
                            color = 'info.main'; // Biru
                          else if (val >= 5)
                            color = 'warning.main'; // Oranye
                          else color = 'error.main'; // Merah
                        }
                        return (
                          <>
                            <OpacityIcon fontSize="small" sx={{ color }} />
                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }} noWrap>
                              {adBluePct} %
                            </Typography>
                          </>
                        );
                      })()}
                    </Stack>
                  </Tooltip>
                )}
              </Box>
            )}
            <Tooltip title={`Driver Name: ${v.driver_name ?? '—'}`}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <AccountBoxOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" noWrap sx={{ width: '100%' }}>
                  {v.driver_name ?? '—'}
                </Typography>
              </Stack>
            </Tooltip>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
